import axios from "axios";
import {
  Client,
  ClientsResponse,
  TeamMember,
  TeamMembersResponse,
  Testimony,
  TestimoniesResponse,
  TestimonyInput,
} from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

// Server origin without the trailing "/api". Stored upload paths already start
// with "/api/uploads/..." (Passenger does NOT strip the BaseURI), so resolving
// them against the base URL — which also ends in "/api" — would double it.
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

// Resolve a stored image value to a displayable URL:
// - empty            -> "" (callers fall back to a placeholder)
// - http(s)/data/blob -> used as-is (legacy Cloudinary/Imgur URLs, local previews)
// - "/api/uploads/.." -> prefixed with the server origin so it points at the API host
export const resolveImageUrl = (value?: string | null): string => {
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return `${API_ORIGIN}${value.startsWith("/") ? "" : "/"}${value}`;
};

// NOTE: no default Content-Type. Axios auto-sets application/json for plain
// object bodies, and lets the browser set multipart/form-data (with boundary)
// when the body is a FormData instance.
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Set while a reload for the host's bot challenge is in flight, so a challenge
// that keeps coming back cannot put the page into a reload loop.
const CHALLENGE_RELOAD_KEY = "challenge-reload";

// Handle auth errors and the host's bot-verification challenge
api.interceptors.response.use(
  (response) => {
    // A real API response got through, so the challenge is cleared; a later
    // one in this session may reload again.
    sessionStorage.removeItem(CHALLENGE_RELOAD_KEY);
    return response;
  },
  (error) => {
    const res = error.response;

    // The challenge answers XHRs with a 403 HTML page that only a full page
    // load can complete. Reload once so the browser can pass it.
    const isChallenge =
      res?.status === 403 &&
      typeof res.data === "string" &&
      res.data.includes("<html");

    if (isChallenge && !sessionStorage.getItem(CHALLENGE_RELOAD_KEY)) {
      sessionStorage.setItem(CHALLENGE_RELOAD_KEY, "1");
      window.location.reload();
      // Never settles: the page is going away, so callers should not render
      // an error state in the meantime.
      return new Promise(() => {});
    }

    if (res?.status === 401) {
      localStorage.removeItem("adminToken");
      window.location.href = "/admin/login";
    }
    return Promise.reject(error);
  },
);

// Auth endpoints
export const authAPI = {
  login: (email: string, password: string) =>
    api.post("/auth/login", { email, password }),
  getProfile: () => api.get("/auth/profile"),
};

// Projects endpoints
export const projectsAPI = {
  getAll: (params?: any) => api.get("/projects", { params }),
  getById: (id: number) => api.get(`/projects/${id}`),
  // Images are uploaded as files, so the body is multipart FormData.
  create: (data: FormData) => api.post("/projects", data),
  update: (id: number, data: FormData) => api.put(`/projects/${id}`, data),
  delete: (id: number) => api.delete(`/projects/${id}`),
  getStats: () => api.get("/projects/stats"),
};

// Team API
export const teamAPI = {
  // Get all team members
  getAll: () => api.get<TeamMembersResponse>("/teams/"),

  // Get single team member
  getById: (id: number) => api.get<{ member: TeamMember }>(`/teams/${id}`),

  // Create team member (multipart: text fields + "image" file)
  create: (data: FormData) => api.post<{ member: TeamMember }>("/teams/", data),

  // Update team member (multipart: text fields + optional "image" file)
  update: (id: number, data: FormData) =>
    api.put<{ member: TeamMember }>(`/teams/${id}`, data),

  // Delete team member
  delete: (id: number) => api.delete(`/teams/${id}`),
};

// Contact API
export const contactAPI = {
  // Submit contact form
  submit: (data: {
    name: string;
    orgName: string;
    phone: string;
    email: string;
    service: string;
    message: string;
  }) => api.post("/contactus", data),
};

export const testimonyAPI = {
  // Get all testimonies (public, newest first)
  getAll: () => api.get<TestimoniesResponse>("/testimonies"),

  // Get single testimony
  getById: (id: number) =>
    api.get<{ testimony: Testimony }>(`/testimonies/${id}`),

  // Create testimony (plain JSON — no image, unlike clients and team members)
  create: (data: TestimonyInput) =>
    api.post<{ message: string; testimony: Testimony }>("/testimonies", data),

  // Update testimony (any subset of the fields)
  update: (id: number, data: Partial<TestimonyInput>) =>
    api.put<{ message: string; testimony: Testimony }>(
      `/testimonies/${id}`,
      data,
    ),

  // Delete testimony
  delete: (id: number) => api.delete<{ message: string }>(`/testimonies/${id}`),
};

export const clientAPI = {
  getAll: () => api.get<ClientsResponse>("/clients"),
  getById: (id: number) => api.get<{ client: Client }>(`/clients/${id}`),
  // Multipart: "name" text field + "image" file
  create: (data: FormData) => api.post("/clients", data),
  update: (id: number, data: FormData) => api.put(`/clients/${id}`, data),
  delete: (id: number) => api.delete(`/clients/${id}`),
};

export default api;
